import React, { useEffect, useState, useMemo } from "react";
import { Button, message, Popconfirm, Tag, Col, Space, Select } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    SyncOutlined
} from "@ant-design/icons";
import Breadcrumbs from "../../component/Breadcrumbs";
import DeliveryServ from "../../service/DeliveriesServ";
import AddDeliveries from "../../component/AddDeliveries";
import EmptyState from "../../util/EmptyState";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton, PrimaryButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import dayjs from "dayjs";

const { Option } = Select;

const DeliveryList = () => {
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [addDeliveriesModel, setDeliveriesModel] = useState(false);
    const [deliverieDetails, setDeliveriesDetails] = useState(null);
    const [totalDeliveries, setTotalDeliveries] = useState(0);
    const [quickFilter, setQuickFilter] = useState(undefined);

    const tenantId = localStorage.getItem("tenantId");

    // 🧠 Fetch Deliveries
    const fetchDeliveries = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                tenantId: tenantId,
                search: searchText,
                startDate: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
                endDate: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined
            };

            const res = await DeliveryServ.index(params);
            console.log("🚚 Deliveries =>", res);

            const data = Array.isArray(res) ? res : res?.data || [];
            setDeliveries(data);
            setTotalDeliveries(res?.total || data.length);
        } catch (err) {
            console.error("Error fetching deliveries:", err);
            message.error("Failed to fetch deliveries");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDeliveries(page);
        }, 500);
        return () => clearTimeout(timer);
    }, [page, pageSize, tenantId, searchText, dateRange]);

    // 🔍 Client-side filter removed in favor of Server-side
    const filteredDeliveries = deliveries;

    const handleAddAddressOpen = () => {
        setDeliveriesDetails(null);
        setIsEditing(false);
        setDeliveriesModel(true);
    };

    const handleEdit = (record) => {
        setDeliveriesDetails(record);
        setIsEditing(true);
        setDeliveriesModel(true);
    };

    const refreshDeliveries = async (isRefresh) => {
        setDeliveriesModel(false);
        if (isRefresh) fetchDeliveries(page);
    };

    // ✅ Remove Address
    const handleDelete = async (id) => {
        try {
            await DeliveryServ.remove(id);
            message.success("Delivery removed successfully");
            fetchDeliveries(page);
        } catch (error) {
            console.error("Error deleting delivery:", error);
            message.error("Failed to remove delivery");
        }
    };

    // 🧾 Table Columns
    const columns = [
        { title: "ID", dataIndex: "id", key: "id", width: 120 },
        { title: "Order ID", key: "orderId", render: (_, record) => record.orderId?.orderId || "N/A" },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            render: (val) => {
                let color = 'blue';
                if (val === 'Express') color = 'red';
                if (val === 'Scheduled') color = 'purple';
                return <Tag color={color}>{val?.toUpperCase()}</Tag>;
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (val) => {
                const s = val?.toLowerCase();
                let color = 'orange'; // Default for Ordered/Packed
                if (s === 'delivered') color = 'green';
                if (s === 'cancelled') color = 'gray';
                if (s === 'out_for_delivery' || s === 'shipped' || s === 'picked_up' || s === 'picked up') color = 'cyan';
                if (s === 'packed') color = 'blue';
                return <Tag color={color}>{val?.replace(/_/g, ' ')?.toUpperCase()}</Tag>;
            }
        },
        {
            title: "Location",
            key: "location",
            width: 300,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ marginBottom: 4 }}>
                        <span style={{ color: '#888', fontWeight: 600 }}>Pickup:</span><br />
                        {record.pickupAddress?.name ? (
                            <span>{record.pickupAddress.name}, {record.pickupAddress.city} - {record.pickupAddress.pincode || record.pickupAddress.zipCode}</span>
                        ) : <span style={{ color: 'red', fontStyle: 'italic' }}>Address Unavailable</span>}
                    </div>
                    <div>
                        <span style={{ color: '#1890ff', fontWeight: 600 }}>Drop:</span><br />
                        <span>{record.deliveryAddress?.city}, {record.deliveryAddress?.district} - {record.deliveryAddress?.pincode}</span>
                    </div>
                </div>
            )
        },
        { title: "Assigned Driver", key: "driver", render: (_, record) => record.assignedDriverId ? `${record.assignedDriverId.firstName} ${record.assignedDriverId.lastName}` : <span style={{ color: 'red' }}>Unassigned</span> },
        {
            title: "Estimated Time",
            dataIndex: "estimatedDeliveryTime",
            key: "estimatedDeliveryTime",
            render: (val) => (val ? new Date(val).toLocaleString() : "—"),
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : "—"),
        },
        {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            ellipsis: true,
        },

        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        type="link"
                    >
                        Manage
                    </Button>
                    <Popconfirm
                        title="Remove this delivery?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger type="link" icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const handleSync = async () => {
        try {
            setLoading(true);
            const res = await DeliveryServ.sync();
            message.success(res.message || "Sync completed");
            fetchDeliveries(page);
        } catch (err) {
            console.error("Sync failed:", err);
            message.error("Sync failed");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllDeliveries = async () => {
        try {
            const params = {
                limit: 2000,
                search: searchText,
                startDate: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
                endDate: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
                tenantId: tenantId
            };
            const res = await DeliveryServ.index(params);
            return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Error fetching all deliveries:", error);
            return [];
        }
    };

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom">
                    <div className="pcu-flex pcu-justify-between pcu-items-start">
                        <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                            Deliveries
                        </h1>
                        <div className="pcu-flex pcu-gap-12 pcu-items-center">
                            <TableActions
                                onFetchFullData={fetchAllDeliveries}
                                columns={columns}
                                fileName="orders"
                                pageName="Orders"
                            />
                            <ResetButton
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    setSearchText("");
                                    setDateRange(null);
                                    setQuickFilter(undefined);
                                    setPage(1);
                                    fetchDeliveries(1);
                                }}
                                text="Refresh"
                            />
                            <PrimaryButton
                                icon={<SyncOutlined />}
                                onClick={handleSync}
                                text="Sync Deliveries"
                            />
                        </div>
                    </div>
                </div>

                <div className="pcu-padding-24">
                    <FilterBar>
                        <Col xs={24} md={8}>
                            <SearchInput
                                label="Search Deliveries"
                                placeholder="Search by ID or Order ID..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <QuickFilter
                                value={quickFilter}
                                onChange={(key, range) => {
                                    setQuickFilter(key);
                                    setDateRange(range);
                                }}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <DateRangePicker
                                label="Custom Date Range"
                                value={dateRange}
                                onChange={(val) => {
                                    setDateRange(val);
                                    setQuickFilter(undefined);
                                }}
                            />
                        </Col>
                    </FilterBar>

                    <div className="pcu-border-top pcu-margin-x-n24">
                        <DataTable
                            data={filteredDeliveries}
                            columns={columns}
                            rowKey="_id"
                            loading={loading}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalDeliveries,
                                showTotal: (total) => `Total ${total} items`,
                                onChange: (p) => setPage(p),
                                onShowSizeChange: (cur, size) => setPageSize(size),
                            }}
                        />
                    </div>

                    {addDeliveriesModel && (
                        <AddDeliveries
                            open={addDeliveriesModel}
                            deliverieDetails={deliverieDetails}
                            isEditing={isEditing}
                            refreshDeliveries={refreshDeliveries}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryList;
